import React from 'react';
import { pickBy } from 'ramda';
import { UnitSystem } from 'providers/api';
import { distanceMetricToImperial, volumeMetricToImperial } from 'utils/unitConversions';
import PCSpool from '../MineModel/Spools/PCSpool';
import { isSpoolActive } from '../MineModel/Spools/utils';
import Vector from '../MineModel/Nodes/Vector';
import { dijkstraShortestPath, NeighborIndex } from '../MineModel/Route/utils';
import { computeRouteVolume, resolveRouteSpools, RouteVolume } from '../MineModel/Route/volumeUtils';
import { useToolHandlers } from '../contexts/ToolHandlerContext';
import { Model } from '../hooks/useModelState';
import {
  getMeasureState,
  subscribeToMeasureState,
  updateMeasureState,
  emptyMeasureResults,
  MeasureLabel
} from '../Scenes/BulkEditScene/Toolbar/measureState';

type DisplayUnit = 'metric' | 'imperial';

interface MeasureControllerProps {
  model: Model;
}

const buildUndirectedNeighbors = (spools: Record<string, PCSpool>): NeighborIndex => {
  const neighbors: NeighborIndex = new Map();
  const addEdge = (a: string, b: string, w: number) => {
    if (!neighbors.has(a)) neighbors.set(a, []);
    neighbors.get(a)!.push({ id: b, w });
  };
  Object.values(spools).forEach((s) => {
    const a = s.pointA?.id;
    const b = s.pointB?.id;
    if (!a || !b) return;
    const w = s.length();
    addEdge(a, b, w);
    addEdge(b, a, w);
  });
  return neighbors;
};

const formatLength = (metres: number, unit: DisplayUnit): string => {
  const value = unit === 'imperial' ? distanceMetricToImperial(metres) : metres;
  const suffix = unit === 'imperial' ? 'ft' : 'm';
  return `${value.toFixed(2)} ${suffix}`;
};

const formatVolume = (cubicMetres: number, unit: DisplayUnit): string => {
  const value = unit === 'imperial' ? volumeMetricToImperial(cubicMetres) : cubicMetres;
  const suffix = unit === 'imperial' ? 'ft³' : 'm³';
  return `${value.toFixed(2)} ${suffix}`;
};

const buildRouteVolumeLabelText = (volume: RouteVolume, unit: DisplayUnit): string => [
  `Volume: ${formatVolume(volume.totalVolumeCubicMetres, unit)}`,
  `Length: ${formatLength(volume.totalLengthMetres, unit)}`,
  ...(volume.unsizedLengthMetres > 0 ? [`(${formatLength(volume.unsizedLengthMetres, unit)} has no pipe size)`] : []),
].join('\n');

const formatSignedDelta = (metres: number, unit: DisplayUnit): string => {
  const value = unit === 'imperial' ? distanceMetricToImperial(metres) : metres;
  const suffix = unit === 'imperial' ? 'ft' : 'm';
  return `${value.toFixed(2)} ${suffix}`;
};

const midpoint = (
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
): { x: number; y: number; z: number } => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
  z: (a.z + b.z) / 2,
});

// Binds the measure tool's canvas click handlers at stage level, so measuring keeps working
// whichever toolbar tab is active. Mount once inside ToolHandlerProvider.
const MeasureController = ({ model }: MeasureControllerProps) => {
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);
  const tools = useToolHandlers();
  const { measureMode, measureActive } = getMeasureState();
  const displayUnit: DisplayUnit = model.project?.unitSystemPreference === UnitSystem.Imperial ? 'imperial' : 'metric';

  React.useEffect(() => subscribeToMeasureState(() => forceUpdate()), []);

  React.useEffect(() => {
    if (!measureActive) {
      return undefined;
    }

    if (measureMode === 'spool-click') {
      const handleMeasureSpoolClick = (spool: PCSpool) => {
        const length = spool.length();
        const a = spool.pointA?.coords;
        const b = spool.pointB?.coords;
        const label: MeasureLabel | null = a && b ? {
          position: midpoint(a, b),
          text: formatLength(length, displayUnit),
          referenceLength: length,
        } : null;
        updateMeasureState({
          ...emptyMeasureResults(),
          spoolResult: length,
          measureLabel: label,
        });
      };
      tools.setSpoolClickHandler(handleMeasureSpoolClick);
      return () => {
        // Only release the handler if another tool has not claimed it since.
        if (tools.spoolClickHandlerRef.current === handleMeasureSpoolClick) {
          tools.setSpoolClickHandler(null);
        }
      };
    }

    if (measureMode === 'node-to-node' || measureMode === 'route' || measureMode === 'route-volume') {
      const handleMeasureNodeClick: Parameters<typeof tools.setNodeClickHandler>[0] = (node) => {
        if (!node) return;
        const currentState = getMeasureState();
        const currentIds = currentState.selectedNodeIds;
        if (currentIds.length === 0) {
          updateMeasureState({ ...emptyMeasureResults(), selectedNodeIds: [node.id] });
          return;
        }
        if (currentIds.length === 1) {
          const firstId = currentIds[0];
          const secondId = node.id;
          const nodeA = model.nodes[firstId];
          const nodeB = model.nodes[secondId];
          if (!nodeA || !nodeB) {
            updateMeasureState({ selectedNodeIds: [secondId] });
            return;
          }
          if (currentState.measureMode === 'node-to-node') {
            const dx = nodeB.coords.x - nodeA.coords.x;
            const dy = nodeB.coords.y - nodeA.coords.y;
            const dz = nodeB.coords.z - nodeA.coords.z;
            const distance = Math.sqrt((dx * dx) + (dy * dy) + (dz * dz));
            const labelText = [
              `ΔX: ${formatSignedDelta(dx, displayUnit)}`,
              `ΔY: ${formatSignedDelta(dy, displayUnit)}`,
              `ΔZ: ${formatSignedDelta(dz, displayUnit)}`,
              `Length: ${formatLength(distance, displayUnit)}`,
            ].join('\n');
            updateMeasureState({
              ...emptyMeasureResults(),
              selectedNodeIds: [firstId, secondId],
              nodeToNodeResult: distance,
              measureLineEndpoints: { aNodeId: firstId, bNodeId: secondId },
              measureLabel: {
                position: midpoint(nodeA.coords, nodeB.coords),
                text: labelText,
                referenceLength: distance,
              },
            });
            return;
          }
          const neighbors = buildUndirectedNeighbors(pickBy(isSpoolActive, model.spools));
          const result = dijkstraShortestPath(neighbors, firstId, secondId);
          if (!result || !Number.isFinite(result.dist)) {
            updateMeasureState({
              ...emptyMeasureResults(),
              selectedNodeIds: [firstId, secondId],
              routeNoPathFound: true,
            });
            return;
          }
          const routeSpools = resolveRouteSpools(result.path, model.spools);
          const pathCoords = result.path
            .map((id) => model.nodes[id]?.coords)
            .filter((c): c is Vector => !!c);
          const centroid = pathCoords.length > 0 ? {
            x: pathCoords.reduce((sum, c) => sum + c.x, 0) / pathCoords.length,
            y: pathCoords.reduce((sum, c) => sum + c.y, 0) / pathCoords.length,
            z: pathCoords.reduce((sum, c) => sum + c.z, 0) / pathCoords.length,
          } : midpoint(nodeA.coords, nodeB.coords);
          const routeVolume = currentState.measureMode === 'route-volume' ? computeRouteVolume(routeSpools) : null;
          updateMeasureState({
            ...emptyMeasureResults(),
            selectedNodeIds: [firstId, secondId],
            routeResult: result.dist,
            routeVolumeResult: routeVolume,
            routeHighlightSpoolIds: routeSpools.map((spool) => spool.spoolId),
            measureLabel: {
              position: centroid,
              text: routeVolume ? buildRouteVolumeLabelText(routeVolume, displayUnit) : formatLength(result.dist, displayUnit),
              referenceLength: result.dist,
            },
          });
          return;
        }
        updateMeasureState({ ...emptyMeasureResults(), selectedNodeIds: [node.id] });
      };
      tools.setNodeClickHandler(handleMeasureNodeClick);
      return () => {
        // Only release the handler if another tool has not claimed it since.
        if (tools.nodeClickHandlerRef.current === handleMeasureNodeClick) {
          tools.setNodeClickHandler(null);
        }
      };
    }

    return undefined;
  }, [measureMode, measureActive, model, tools, displayUnit]);

  return null;
};

export default MeasureController;
